import { createFileRoute, Link } from "@tanstack/react-router";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL = "https://vendingmachinefree.com/vending-machine-placement";
const TITLE = "Vending Machine Placement — USA";
const DESC = "Professional vending machine placement across the USA. Fast install, modern machines, cashless payments, weekly service. Free placement available for qualifying businesses.";

const FAQS = [
  { q: "What is vending machine placement?", a: "Placement means an operator installs, stocks, and services vending machines at your location. Most US businesses qualify for 100% free placement." },
  { q: "How much does vending machine placement cost?", a: "For qualifying businesses, placement is $0 — no install fee, no service fee, no repair charges. We earn revenue from product sales." },
  { q: "Can I get multiple machines at one location?", a: "Yes. High-traffic sites often receive multi-machine setups combining drink, snack, coffee, and food machines." },
  { q: "Do your machines accept cashless payments?", a: "Yes. Modern machines support credit/debit cards, mobile wallets (Apple Pay, Google Pay), and cash." },
];

export const Route = createFileRoute("/vending-machine-placement")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Vending Machine Placement",
          provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
          areaServed: { "@type": "Country", name: "United States" },
          serviceType: "Vending Machine Placement & Service",
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
            { "@type": "ListItem", position: 2, name: "Vending Machine Placement", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-steel text-white py-16">
        <div className="container-x">
          <h1 className="font-display text-4xl md:text-5xl uppercase">Vending Machine Placement — Professional Install & Full Service</h1>
          <p className="mt-4 max-w-3xl text-lg opacity-90">
            We provide vending machine placement for businesses of all sizes across the USA. Whether you need one machine or a full vending setup, our operators deliver, install, stock, and service everything.
          </p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("vending_machine_placement_hero")} className="inline-block mt-6 bg-safety px-6 py-3 font-semibold uppercase">Request Placement</Link>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl uppercase">Placement Options</h2>
            <ul className="mt-4 space-y-2 list-disc pl-5">
              <li><Link className="hover:text-safety" to="/free-vending-machine-placement">Free placement (most businesses qualify)</Link></li>
              <li>Multi-machine setups</li>
              <li>High-traffic locations</li>
              <li>Custom machine mixes</li>
              <li>Breakroom solutions</li>
              <li>Industrial vending</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase">Why Our Placement Works</h2>
            <ul className="mt-4 space-y-2 list-disc pl-5">
              <li>Fast installation</li><li>Modern machines</li>
              <li>Cashless payment options</li><li>Weekly servicing</li>
              <li>Reliable local operators</li><li>Nationwide coverage</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl uppercase">Industries Covered</h2>
            <ul className="mt-4 grid grid-cols-2 gap-1 list-disc pl-5">
              {([
                ["Offices","offices-corporate"],
                ["Warehouses","warehouses-manufacturing"],
                ["Schools","schools-universities"],
                ["Hospitals","hospitals-clinics"],
                ["Hotels","hotels-hospitality"],
                ["Gyms","gyms-fitness-centers"],
                ["Manufacturing","warehouses-manufacturing"],
                ["Logistics","warehouses-manufacturing"],
                ["Airports","airports-aviation"],
                ["Military Bases","military-bases"],
                ["Mines","mines-mining-sites"],
              ] as const).map(([label,slug])=>(
                <li key={label}><Link className="hover:text-safety" to="/industries/$industry" params={{industry:slug}}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase">Machine Types</h2>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {["Drink","Snack","Combo","Coffee","Food","Ice"].map(m=>(
                <Link key={m} to="/vending-machines" className="border p-3 hover:border-safety">{m}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase">Nationwide Coverage</h2>
          <p className="mt-2">Vending machine placement in all 50 states — major metros and regional hubs.</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-x-4 gap-y-1 text-sm">
            {STATES_DATA.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(s=>(
              <Link key={s.code} to="/states/$state" params={{state:s.slug}} className="hover:text-safety">{s.name}</Link>
            ))}
          </div>
          <p className="mt-6 text-sm">
            Related: <Link to="/free-vending-machines-usa" className="text-safety underline">Free Vending Machines USA</Link> · <Link to="/free-vending-machine-placement" className="text-safety underline">Free Placement</Link> · <Link to="/coverage" className="text-safety underline">Coverage Map</Link>
          </p>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-2xl uppercase">FAQs</h2>
          <div className="mt-4 space-y-4">
            {FAQS.map(f=>(
              <div key={f.q}><h3 className="font-semibold">{f.q}</h3><p className="text-muted-foreground">{f.a}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel text-white py-12 text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Request Placement</h2>
          <p className="mt-2 opacity-90">Get vending machines installed at your business — fast, free, and fully serviced.</p>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("vending_machine_placement_footer")} className="inline-block mt-6 bg-safety px-8 py-3 font-semibold uppercase">Apply Now</Link>
        </div>
      </section>
    </div>
  );
}
